import { useState, useEffect, useCallback } from 'react';
import {
  fetchEstimates, fetchEstimate, createEstimate, updateEstimate, deleteEstimate,
  changeEstimateStatus, cloneEstimate, exportEstimate, addAttendance, updateAttendance, deleteAttendance,
  addDestajo, updateDestajo, deleteDestajo,
  fetchPersonnel, createPerson, updatePerson, deactivatePerson,
  fetchContracts, fetchContract, createContract, updateContract,
  fetchProperties, fetchWorkCatalog,
} from '@/lib/api';
import { formatMXN, formatMXNCompact, formatDate } from '@/lib/formatters';
import { useAuth } from '@/lib/AuthContext';
import { PageHeader } from '@/components/common/PageHeader';
import { KPICard } from '@/components/common/KPICard';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogClose } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import {
  Plus, Pencil, Trash2, ClipboardList, Users, HardHat,
  CheckCircle2, Clock, DollarSign, ChevronLeft, AlertTriangle, FileText, Copy, Download,
} from 'lucide-react';

const STATUS_CFG = {
  draft:          { label: 'En Cálculo',        cls: 'bg-slate-100 text-slate-600 border-slate-300', icon: Clock,          phase: 'Cálculo' },
  approved:       { label: 'Aprobada',           cls: 'bg-emerald-50 text-emerald-700 border-emerald-200', icon: CheckCircle2, phase: 'Aprobación' },
  partially_paid: { label: 'Pago Parcial',       cls: 'bg-amber-50 text-amber-700 border-amber-200', icon: DollarSign,     phase: 'Pago' },
  paid:           { label: 'Pagada',             cls: 'bg-blue-50 text-blue-700 border-blue-200', icon: CheckCircle2,       phase: 'Pago' },
  cancelled:      { label: 'Cancelada',          cls: 'bg-red-50 text-red-600 border-red-200', icon: AlertTriangle,         phase: 'Cancelada' },
};

const PHASE_STEPS = [
  { key: 'calc',     label: 'Cálculo',    desc: 'Captura de montos', statuses: ['draft'] },
  { key: 'approval', label: 'Aprobación', desc: 'Revisión y visto bueno', statuses: ['approved'] },
  { key: 'payment',  label: 'Pago Real',  desc: 'Pago efectuado fuera del sistema', statuses: ['partially_paid', 'paid'] },
];

export default function Estimaciones() {
  const { can } = useAuth();
  const canEdit = can('estimaciones', 'edit');
  const canAdmin = can('estimaciones', 'admin');
  const [mainTab, setMainTab] = useState('estimates');

  return (
    <div className="space-y-6" data-testid="estimaciones-page">
      <Tabs value={mainTab} onValueChange={setMainTab}>
        <div className="flex items-center justify-between">
          <TabsList>
            <TabsTrigger value="estimates"><ClipboardList className="h-3.5 w-3.5 mr-1.5" />Estimaciones</TabsTrigger>
            <TabsTrigger value="personnel"><Users className="h-3.5 w-3.5 mr-1.5" />Personal</TabsTrigger>
            <TabsTrigger value="contracts"><HardHat className="h-3.5 w-3.5 mr-1.5" />Destajos Pactados</TabsTrigger>
          </TabsList>
        </div>
        <TabsContent value="estimates"><EstimatesList canEdit={canEdit} canAdmin={canAdmin} /></TabsContent>
        <TabsContent value="personnel"><PersonnelList canEdit={canEdit} /></TabsContent>
        <TabsContent value="contracts"><ContractsList canEdit={canEdit} /></TabsContent>
      </Tabs>
    </div>
  );
}


/* ══════════════════════════════════════════════
   ESTIMATES LIST
   ══════════════════════════════════════════════ */
function EstimatesList({ canEdit, canAdmin }) {
  const [estimates, setEstimates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);

  const loadList = useCallback(async () => {
    try { setEstimates(await fetchEstimates()); }
    catch { toast.error('Error al cargar estimaciones'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadList(); }, [loadList]);

  const openDetail = async (est) => {
    setDetailLoading(true);
    try { setSelected(await fetchEstimate(est.id)); }
    catch { toast.error('Error al cargar detalle'); }
    finally { setDetailLoading(false); }
  };

  const handleCreated = (est) => { setCreateOpen(false); openDetail(est); loadList(); };
  const closeDetail = () => { setSelected(null); loadList(); };
  const refreshDetail = async () => { if (selected) setSelected(await fetchEstimate(selected.id)); };

  if (selected) {
    return (
      <DetailView
        estimate={selected}
        loading={detailLoading}
        onClose={closeDetail}
        onRefresh={refreshDetail}
        canEdit={canEdit}
        canAdmin={canAdmin}
      />
    );
  }

  // KPIs
  const drafts = estimates.filter(e => e.status === 'draft').length;
  const approved = estimates.filter(e => e.status === 'approved').length;
  const totalEstimated = estimates.filter(e => e.status !== 'cancelled').reduce((s, e) => s + (e.total_estimate || 0), 0);

  if (loading) return (
    <div className="space-y-6"><Skeleton className="h-10 w-64" /><div className="grid grid-cols-3 gap-4">{[1,2,3].map(i => <Skeleton key={i} className="h-28 rounded-xl" />)}</div><Skeleton className="h-[400px] rounded-xl" /></div>
  );

  return (
    <div className="space-y-6" data-testid="estimaciones-page">
      <PageHeader title="Estimaciones" subtitle={`${estimates.length} estimaciones semanales`}>
        {canEdit && <Button onClick={() => setCreateOpen(true)} data-testid="create-estimate-btn"><Plus className="h-4 w-4 mr-1.5" /> Nueva Estimación</Button>}
      </PageHeader>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <KPICard label="Borradores" value={drafts} icon={Clock} compact testId="kpi-drafts" />
        <KPICard label="Aprobadas" value={approved} icon={CheckCircle2} compact testId="kpi-approved" />
        <KPICard label="Total Estimado" value={formatMXNCompact(totalEstimated)} icon={DollarSign} compact testId="kpi-total" />
        <KPICard label="Total" value={estimates.length} icon={FileText} compact testId="kpi-count" />
      </div>

      {estimates.length === 0 ? (
        <Card className="rounded-xl border bg-white"><CardContent className="py-16 text-center">
          <ClipboardList className="h-14 w-14 text-slate-200 mx-auto mb-4" />
          <p className="text-sm font-medium text-slate-600 mb-1">Sin estimaciones</p>
          <p className="text-xs text-slate-400">Crea la primera estimación semanal para empezar.</p>
        </CardContent></Card>
      ) : (
        <Card className="rounded-xl border bg-white overflow-clip">
          <Table data-testid="estimates-table">
            <TableHeader className="sticky top-0 z-20">
              <TableRow className="bg-slate-50 shadow-[0_1px_0_0_rgb(226,232,240)]">
                <TableHead className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold py-3 px-3">Semana</TableHead>
                <TableHead className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold py-3 px-3">Periodo</TableHead>
                <TableHead className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold py-3 px-3 text-center">Personal</TableHead>
                <TableHead className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold py-3 px-3 text-center">Destajos</TableHead>
                <TableHead className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold py-3 px-3 text-right">Monto Personal</TableHead>
                <TableHead className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold py-3 px-3 text-right">Monto Destajos</TableHead>
                <TableHead className="text-[10px] uppercase tracking-widest text-[#F05A28] font-semibold py-3 px-3 text-right">Total</TableHead>
                <TableHead className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold py-3 px-3">Estatus</TableHead>
                <TableHead className="w-8" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {estimates.map(e => {
                const st = STATUS_CFG[e.status] || STATUS_CFG.draft;
                return (
                  <TableRow key={e.id} className="table-row-hover group cursor-pointer" onClick={() => openDetail(e)} data-testid={`estimate-row-${e.id}`}>
                    <TableCell className="py-2.5 px-3 text-[13px] font-semibold">{e.week_label}</TableCell>
                    <TableCell className="py-2.5 px-3 text-[11px] text-slate-500">{formatDate(e.start_date)} — {formatDate(e.end_date)}</TableCell>
                    <TableCell className="py-2.5 px-3 text-center text-[12px] tabular-nums">{e.attendance_count}</TableCell>
                    <TableCell className="py-2.5 px-3 text-center text-[12px] tabular-nums">{e.destajos_count}</TableCell>
                    <TableCell className="py-2.5 px-3 text-right text-[12px] tabular-nums">{formatMXNCompact(e.total_attendance)}</TableCell>
                    <TableCell className="py-2.5 px-3 text-right text-[12px] tabular-nums">{formatMXNCompact(e.total_destajos)}</TableCell>
                    <TableCell className="py-2.5 px-3 text-right text-[13px] tabular-nums font-bold text-[#F05A28]">{formatMXNCompact(e.total_estimate)}</TableCell>
                    <TableCell className="py-2.5 px-3"><Badge variant="outline" className={`${st.cls} text-[9px]`}>{st.label}</Badge></TableCell>
                    <TableCell className="py-1 px-2"><ChevronLeft className="h-3.5 w-3.5 text-slate-300 rotate-180" /></TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Card>
      )}

      <CreateEstimateDialog open={createOpen} onClose={() => setCreateOpen(false)} onCreated={handleCreated} />
    </div>
  );
}


/* ══════════════════════════════════════════════
   CREATE DIALOG
   ══════════════════════════════════════════════ */
function CreateEstimateDialog({ open, onClose, onCreated }) {
  const [form, setForm] = useState({ week_label: '', start_date: '', end_date: '', notes: '' });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSave = async () => {
    if (!form.week_label || !form.start_date || !form.end_date) return;
    setSaving(true);
    try {
      const est = await createEstimate(form);
      toast.success('Estimación creada');
      onCreated(est);
      setForm({ week_label: '', start_date: '', end_date: '', notes: '' });
    } catch (err) { toast.error(err?.response?.data?.detail || 'Error'); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={v => { if (!v) onClose(); }}>
      <DialogContent className="sm:max-w-md" data-testid="create-estimate-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><ClipboardList className="h-5 w-5 text-[#F05A28]" /> Nueva Estimación Semanal</DialogTitle></DialogHeader>
        <div className="space-y-4 py-2">
          <div><Label className="text-xs">Etiqueta de semana</Label><Input value={form.week_label} onChange={e => set('week_label', e.target.value)} placeholder="Semana 16 — Abr 2026" className="mt-1 h-9" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs">Fecha inicio</Label><Input type="date" value={form.start_date} onChange={e => set('start_date', e.target.value)} className="mt-1 h-9 text-sm" data-testid="est-start-date" /></div>
            <div><Label className="text-xs">Fecha fin</Label><Input type="date" value={form.end_date} onChange={e => set('end_date', e.target.value)} className="mt-1 h-9 text-sm" data-testid="est-end-date" /></div>
          </div>
          <div><Label className="text-xs">Notas</Label><Input value={form.notes} onChange={e => set('notes', e.target.value)} placeholder="Opcional" className="mt-1 h-9" /></div>
        </div>
        <DialogFooter>
          <DialogClose asChild><Button variant="outline">Cancelar</Button></DialogClose>
          <Button onClick={handleSave} disabled={saving || !form.week_label || !form.start_date}>{saving ? 'Creando...' : 'Crear Estimación'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


/* ══════════════════════════════════════════════
   DETAIL VIEW
   ══════════════════════════════════════════════ */
function DetailView({ estimate, loading, onClose, onRefresh, canEdit, canAdmin }) {
  const e = estimate;
  const st = STATUS_CFG[e.status] || STATUS_CFG.draft;
  const isDraft = e.status === 'draft';
  const isEditable = (isDraft || e.status === 'approved') && canEdit;
  const canClone = ['approved', 'partially_paid', 'paid'].includes(e.status) && canEdit;
  const canExport = ['approved', 'partially_paid', 'paid'].includes(e.status);
  const [cloneOpen, setCloneOpen] = useState(false);

  const handleStatus = async (status) => {
    try {
      await changeEstimateStatus(e.id, status);
      toast.success(`Estatus cambiado a ${STATUS_CFG[status]?.label || status}`);
      onRefresh();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Error'); }
  };

  const handleDelete = async () => {
    if (!confirm('¿Eliminar esta estimación y todos sus registros?')) return;
    try { await deleteEstimate(e.id); toast.success('Eliminada'); onClose(); }
    catch { toast.error('Error'); }
  };

  return (
    <div className="space-y-6" data-testid="estimate-detail">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="icon" className="h-8 w-8" onClick={onClose}><ChevronLeft className="h-4 w-4" /></Button>
        <div className="flex-1">
          <h1 className="text-[22px] font-semibold tracking-tight text-slate-900" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>{e.week_label}</h1>
          <p className="text-[13px] text-slate-400">{formatDate(e.start_date)} — {formatDate(e.end_date)}</p>
        </div>
        <Badge variant="outline" className={`${st.cls} text-xs px-3 py-1`}>{st.label}</Badge>
      </div>

      {/* Phase Stepper */}
      <div className="flex items-center gap-0 bg-white border rounded-xl p-4">
        {PHASE_STEPS.map((step, i) => {
          const isActive = step.statuses.includes(e.status);
          const isPast = (step.key === 'calc' && ['approved','partially_paid','paid'].includes(e.status))
                      || (step.key === 'approval' && ['partially_paid','paid'].includes(e.status));
          return (
            <div key={step.key} className="flex items-center flex-1">
              <div className={`flex flex-col items-center flex-1 ${isActive ? '' : isPast ? 'opacity-60' : 'opacity-30'}`}>
                <div className={`h-8 w-8 rounded-full flex items-center justify-center text-xs font-bold ${isActive ? 'bg-[#F05A28] text-white' : isPast ? 'bg-emerald-500 text-white' : 'bg-slate-200 text-slate-500'}`}>
                  {isPast ? <CheckCircle2 className="h-4 w-4" /> : i + 1}
                </div>
                <p className={`text-[11px] font-semibold mt-1.5 ${isActive ? 'text-[#F05A28]' : 'text-slate-500'}`}>{step.label}</p>
                <p className="text-[9px] text-slate-400">{step.desc}</p>
              </div>
              {i < PHASE_STEPS.length - 1 && <div className={`h-px w-8 shrink-0 ${isPast ? 'bg-emerald-300' : 'bg-slate-200'}`} />}
            </div>
          );
        })}
      </div>

      {/* Disclaimer */}
      <div className="bg-slate-50 border border-slate-200 rounded-lg px-4 py-2.5 flex items-center gap-3">
        <AlertTriangle className="h-4 w-4 text-slate-400 shrink-0" />
        <p className="text-[10px] text-slate-500 leading-relaxed">
          <strong>Este módulo es independiente del flujo financiero.</strong> Estimado no significa pagado. Aprobado no genera egresos. El pago se registra como referencia operativa — los egresos reales se capturan por separado en el módulo de Egresos.
        </p>
      </div>

      {/* Summary KPIs */}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        <Card className="rounded-xl border bg-white"><CardContent className="p-3.5 text-center">
          <p className="text-[9px] uppercase tracking-widest text-slate-400">Personal</p>
          <p className="text-[17px] font-bold tabular-nums text-slate-700" style={{ fontFamily: 'Space Grotesk' }}>{formatMXNCompact(e.total_attendance)}</p>
          <p className="text-[10px] text-slate-400">{e.attendance_count} trabajador{e.attendance_count !== 1 ? 'es' : ''}</p>
        </CardContent></Card>
        <Card className="rounded-xl border bg-white"><CardContent className="p-3.5 text-center">
          <p className="text-[9px] uppercase tracking-widest text-slate-400">Destajos</p>
          <p className="text-[17px] font-bold tabular-nums text-slate-700" style={{ fontFamily: 'Space Grotesk' }}>{formatMXNCompact(e.total_destajos)}</p>
          <p className="text-[10px] text-slate-400">{e.destajos_count} concepto{e.destajos_count !== 1 ? 's' : ''}</p>
        </CardContent></Card>
        <Card className="rounded-xl border bg-white border-[#F05A28]/20"><CardContent className="p-3.5 text-center">
          <p className="text-[9px] uppercase tracking-widest text-[#F05A28]">Total Estimación</p>
          <p className="text-[17px] font-bold tabular-nums text-[#F05A28]" style={{ fontFamily: 'Space Grotesk' }}>{formatMXNCompact(e.total_estimate)}</p>
          <p className="text-[9px] text-slate-400 mt-0.5">Solo estimado — no afecta flujo</p>
        </CardContent></Card>
      </div>

      {/* Actions */}
      <div className="flex items-center gap-2 flex-wrap">
        {isDraft && canAdmin && <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" onClick={() => handleStatus('approved')}><CheckCircle2 className="h-3.5 w-3.5 mr-1.5" /> Aprobar Estimación</Button>}
        {e.status === 'approved' && canAdmin && (
          <>
            <Button size="sm" onClick={() => handleStatus('paid')}><DollarSign className="h-3.5 w-3.5 mr-1.5" /> Marcar como Pagada</Button>
            <Button size="sm" variant="outline" onClick={() => handleStatus('partially_paid')}>Pago Parcial</Button>
            <Button size="sm" variant="ghost" className="text-slate-500" onClick={() => handleStatus('draft')}>Regresar a Borrador</Button>
          </>
        )}
        {e.status === 'partially_paid' && canAdmin && (
          <>
            <Button size="sm" onClick={() => handleStatus('paid')}><DollarSign className="h-3.5 w-3.5 mr-1.5" /> Marcar como Pagada</Button>
            <Button size="sm" variant="ghost" className="text-slate-500" onClick={() => handleStatus('approved')}>Regresar a Aprobada</Button>
          </>
        )}
        {e.status === 'paid' && canAdmin && (
          <Button size="sm" variant="ghost" className="text-slate-500" onClick={() => handleStatus('approved')}>Revertir Pago</Button>
        )}
        {canClone && <Button size="sm" variant="outline" onClick={() => setCloneOpen(true)}><Copy className="h-3.5 w-3.5 mr-1.5" /> Clonar para Siguiente Semana</Button>}
        {canExport && <Button size="sm" variant="outline" onClick={() => exportEstimate(e.id).catch(() => toast.error('Error al exportar'))}><Download className="h-3.5 w-3.5 mr-1.5" /> Exportar para Pago</Button>}
        {isDraft && canEdit && <Button size="sm" variant="ghost" className="text-red-500 ml-auto" onClick={handleDelete}><Trash2 className="h-3.5 w-3.5 mr-1.5" /> Eliminar</Button>}
      </div>

      {/* Tabs */}
      <Tabs defaultValue="personal" className="space-y-4">
        <TabsList>
          <TabsTrigger value="personal"><Users className="h-3.5 w-3.5 mr-1.5" />Personal ({e.attendance_count})</TabsTrigger>
          <TabsTrigger value="destajos"><HardHat className="h-3.5 w-3.5 mr-1.5" />Destajos ({e.destajos_count})</TabsTrigger>
        </TabsList>

        <TabsContent value="personal">
          <AttendanceTab estimateId={e.id} records={e.attendance || []} editable={isEditable} onChanged={onRefresh} />
        </TabsContent>
        <TabsContent value="destajos">
          <DestajoTab estimateId={e.id} records={e.destajos || []} editable={isEditable} onChanged={onRefresh} />
        </TabsContent>
      </Tabs>

      {/* Approval info */}
      {e.approved_by && (
        <div className="text-[10px] text-slate-400 text-right">
          Aprobada por {e.approved_by} el {e.approved_at ? formatDate(e.approved_at.substring(0, 10)) : '—'}
        </div>
      )}

      {/* Clone Dialog */}
      {cloneOpen && (
        <CloneEstimateDialog
          source={e}
          onClose={() => setCloneOpen(false)}
          onCloned={(newEst) => { setCloneOpen(false); onClose(); }}
        />
      )}
    </div>
  );
}


/* ══════════════════════════════════════════════
   CLONE DIALOG
   ══════════════════════════════════════════════ */
function CloneEstimateDialog({ source, onClose, onCloned }) {
  // Auto-calculate next week dates
  const srcEnd = new Date(source.end_date);
  const nextStart = new Date(srcEnd); nextStart.setDate(nextStart.getDate() + 1);
  const nextEnd = new Date(nextStart); nextEnd.setDate(nextEnd.getDate() + 6);
  const fmt = (d) => d.toISOString().substring(0, 10);

  const [form, setForm] = useState({
    week_label: source.week_label.replace(/\d+/, (n) => parseInt(n) + 1),
    start_date: fmt(nextStart),
    end_date: fmt(nextEnd),
    notes: '',
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleClone = async () => {
    setSaving(true);
    try {
      const est = await cloneEstimate(source.id, form);
      toast.success(`Estimación clonada: ${form.week_label}`);
      onCloned(est);
    } catch (err) { toast.error(err?.response?.data?.detail || 'Error al clonar'); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={v => { if (!v) onClose(); }}>
      <DialogContent className="sm:max-w-md" data-testid="clone-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Copy className="h-5 w-5 text-[#F05A28]" /> Clonar Estimación</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="bg-slate-50 rounded-lg p-3 text-xs space-y-1">
            <p className="text-[9px] uppercase tracking-widest text-slate-400 mb-1">Se clonará desde</p>
            <div className="flex justify-between"><span className="text-slate-500">Semana</span><span className="font-medium">{source.week_label}</span></div>
            <div className="flex justify-between"><span className="text-slate-500">Personal</span><span className="tabular-nums">{source.attendance_count} trabajadores</span></div>
            <div className="flex justify-between"><span className="text-slate-500">Destajos</span><span className="tabular-nums">{source.destajos_count} conceptos</span></div>
          </div>
          <div className="bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 text-[10px] text-amber-700 flex items-center gap-2">
            <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
            Los destajos se clonan con el avance actual como nuevo avance anterior.
          </div>
          <div><Label className="text-xs">Etiqueta de la nueva semana</Label><Input value={form.week_label} onChange={e => set('week_label', e.target.value)} className="mt-1" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs">Fecha inicio</Label><Input type="date" value={form.start_date} onChange={e => set('start_date', e.target.value)} className="mt-1" /></div>
            <div><Label className="text-xs">Fecha fin</Label><Input type="date" value={form.end_date} onChange={e => set('end_date', e.target.value)} className="mt-1" /></div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button onClick={handleClone} disabled={saving || !form.week_label || !form.start_date}>{saving ? 'Clonando...' : 'Clonar Estimación'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


/* ══════════════════════════════════════════════
   ATTENDANCE TAB
   ══════════════════════════════════════════════ */
function AttendanceTab({ estimateId, records, editable, onChanged }) {
  const [addOpen, setAddOpen] = useState(false);
  const [editTarget, setEditTarget] = useState(null);

  const handleDelete = async (id) => {
    try { await deleteAttendance(id); toast.success('Eliminado'); onChanged(); }
    catch { toast.error('Error'); }
  };

  return (
    <div className="space-y-3">
      {editable && <Button size="sm" variant="outline" onClick={() => setAddOpen(true)}><Plus className="h-3.5 w-3.5 mr-1" /> Agregar Trabajador</Button>}

      {/* Resumen por cuadrilla */}
      {records.length > 0 && (() => {
        const byCrew = {};
        records.forEach(r => {
          const crew = r.crew || 'Sin cuadrilla';
          if (!byCrew[crew]) byCrew[crew] = { count: 0, total: 0 };
          byCrew[crew].count++;
          byCrew[crew].total += r.total_estimated_pay || 0;
        });
        const crewSort = (a, b) => {
          const aIsQuru = a[0].toLowerCase().includes('quru');
          const bIsQuru = b[0].toLowerCase().includes('quru');
          if (aIsQuru && !bIsQuru) return -1;
          if (!aIsQuru && bIsQuru) return 1;
          return a[0].localeCompare(b[0], 'es');
        };
        const crews = Object.entries(byCrew).sort(crewSort);
        return (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
            {crews.map(([crew, data]) => (
              <div key={crew} className="bg-white border rounded-lg px-3 py-2.5 flex items-center justify-between">
                <div>
                  <p className="text-[11px] font-medium text-slate-700">{crew}</p>
                  <p className="text-[9px] text-slate-400">{data.count} trabajador{data.count !== 1 ? 'es' : ''}</p>
                </div>
                <p className="text-[13px] font-bold tabular-nums text-[#F05A28]" style={{ fontFamily: 'Space Grotesk' }}>{formatMXNCompact(data.total)}</p>
              </div>
            ))}
          </div>
        );
      })()}

      {records.length === 0 ? (
        <Card className="rounded-xl border bg-white"><CardContent className="py-8 text-center"><Users className="h-8 w-8 text-slate-200 mx-auto mb-2" /><p className="text-sm text-slate-400">Sin registros de personal</p></CardContent></Card>
      ) : (
        <Card className="rounded-xl border bg-white overflow-clip">
          <Table>
            <TableHeader className="sticky top-0 z-20">
              <TableRow className="bg-slate-50 shadow-[0_1px_0_0_rgb(226,232,240)]">
                <TableHead className="text-[9px] uppercase tracking-widest text-slate-400 font-semibold py-2.5 px-3">Trabajador</TableHead>
                <TableHead className="text-[9px] uppercase tracking-widest text-slate-400 font-semibold py-2.5 px-3">Cuadrilla</TableHead>
                <TableHead className="text-[9px] uppercase tracking-widest text-slate-400 font-semibold py-2.5 px-3 text-right">Sueldo Sem.</TableHead>
                <TableHead className="text-[9px] uppercase tracking-widest text-slate-400 font-semibold py-2.5 px-3 text-center">Días</TableHead>
                <TableHead className="text-[9px] uppercase tracking-widest text-slate-400 font-semibold py-2.5 px-3 text-right">Hrs +/-</TableHead>
                <TableHead className="text-[9px] uppercase tracking-widest text-[#F05A28] font-semibold py-2.5 px-3 text-right">Total</TableHead>
                {editable && <TableHead className="w-16" />}
              </TableRow>
            </TableHeader>
            <TableBody>
              {[...records].sort((a, b) => {
                const ac = (a.crew || 'zzz').toLowerCase();
                const bc = (b.crew || 'zzz').toLowerCase();
                const aQ = ac.includes('quru');
                const bQ = bc.includes('quru');
                if (aQ && !bQ) return -1;
                if (!aQ && bQ) return 1;
                if (ac !== bc) return ac.localeCompare(bc, 'es');
                return (a.worker_name || '').localeCompare(b.worker_name || '', 'es');
              }).map(r => (
                <TableRow key={r.id} className="group">
                  <TableCell className="py-2 px-3 text-[12px] font-medium">{r.worker_name}</TableCell>
                  <TableCell className="py-2 px-3 text-[11px] text-slate-500">{r.crew || '—'}</TableCell>
                  <TableCell className="py-2 px-3 text-right text-[11px] tabular-nums">{formatMXN(r.weekly_salary)}</TableCell>
                  <TableCell className="py-2 px-3 text-center text-[11px] tabular-nums">{r.days_attended}</TableCell>
                  <TableCell className="py-2 px-3 text-right text-[11px] tabular-nums">{(() => { const hrs = (r.overtime_hours || 0) - (r.undertime_hours || 0); return hrs !== 0 ? <span className={hrs > 0 ? 'text-emerald-600' : 'text-red-500'}>{hrs > 0 ? '+' : ''}{hrs}</span> : '—'; })()}</TableCell>
                  <TableCell className="py-2 px-3 text-right text-[12px] tabular-nums font-bold text-[#F05A28]">{formatMXN(r.total_estimated_pay)}</TableCell>
                  {editable && <TableCell className="py-2 px-3">
                    <div className="flex gap-0.5 opacity-0 group-hover:opacity-100">
                      <Button variant="ghost" size="icon" className="h-6 w-6 text-slate-400" onClick={() => setEditTarget(r)}><Pencil className="h-3 w-3" /></Button>
                      <Button variant="ghost" size="icon" className="h-6 w-6 text-slate-400 hover:text-red-500" onClick={() => handleDelete(r.id)}><Trash2 className="h-3 w-3" /></Button>
                    </div>
                  </TableCell>}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}
      {(addOpen || editTarget) && (
        <AttendanceForm
          estimateId={estimateId}
          initial={editTarget}
          onClose={() => { setAddOpen(false); setEditTarget(null); }}
          onSaved={() => { setAddOpen(false); setEditTarget(null); onChanged(); }}
        />
      )}
    </div>
  );
}

function AttendanceForm({ estimateId, initial, onClose, onSaved }) {
  const isEdit = !!initial;
  const [personnel, setPersonnel] = useState([]);
  const [form, setForm] = useState(() => {
    if (initial) {
      const hrs = (initial.overtime_hours || 0) - (initial.undertime_hours || 0);
      return { ...initial, extra_hours: hrs };
    }
    return { worker_id: '', worker_name: '', crew: '', weekly_salary: '', days_attended: 6, extra_hours: 0 };
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  useEffect(() => { fetchPersonnel(true).then(setPersonnel).catch(() => {}); }, []);

  const selectWorker = (workerId) => {
    const p = personnel.find(x => x.id === workerId);
    if (p) {
      setForm(f => ({ ...f, worker_id: p.id, worker_name: p.full_name, crew: p.crew || '', weekly_salary: p.weekly_salary || '' }));
    }
  };

  // Calculate preview
  const salary = parseFloat(form.weekly_salary) || 0;
  const days = parseFloat(form.days_attended) || 0;
  const extraHrs = parseFloat(form.extra_hours) || 0;
  const dailyRate = salary > 0 ? salary / 6 : 0;
  const hourlyRate = dailyRate / 8;
  const basePay = dailyRate * days;
  const extraPay = hourlyRate * extraHrs;
  const totalPreview = Math.round((basePay + extraPay) * 100) / 100;

  const handleSave = async () => {
    setSaving(true);
    try {
      const d = {
        worker_id: form.worker_id || null,
        worker_name: form.worker_name,
        crew: form.crew,
        weekly_salary: salary,
        days_attended: days,
        overtime_hours: extraHrs > 0 ? extraHrs : 0,
        undertime_hours: extraHrs < 0 ? Math.abs(extraHrs) : 0,
        overtime_amount: extraHrs > 0 ? extraPay : 0,
        discount_amount: extraHrs < 0 ? Math.abs(extraPay) : 0,
        adjustment_amount: 0,
      };
      if (isEdit) await updateAttendance(initial.id, d);
      else await addAttendance(estimateId, d);
      toast.success(isEdit ? 'Actualizado' : 'Agregado');
      onSaved();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Error'); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open onOpenChange={v => { if (!v) onClose(); }}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader><DialogTitle>{isEdit ? 'Editar' : 'Agregar'} Trabajador</DialogTitle></DialogHeader>
        <div className="space-y-3 py-2">
          {!isEdit && personnel.length > 0 && (
            <div>
              <Label className="text-xs">Seleccionar de lista de personal</Label>
              <Select value={form.worker_id} onValueChange={selectWorker}>
                <SelectTrigger className="mt-1 h-8 text-xs"><SelectValue placeholder="Seleccionar trabajador..." /></SelectTrigger>
                <SelectContent>
                  {personnel.map(p => <SelectItem key={p.id} value={p.id}>{p.full_name} — {p.crew || p.role_or_specialty || ''} ({formatMXN(p.weekly_salary)})</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs">Nombre</Label><Input value={form.worker_name} onChange={e => set('worker_name', e.target.value)} className="mt-1 h-8 text-sm" /></div>
            <div><Label className="text-xs">Cuadrilla</Label><Input value={form.crew} onChange={e => set('crew', e.target.value)} className="mt-1 h-8 text-sm" readOnly={!!form.worker_id} /></div>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div><Label className="text-xs">Sueldo Semanal</Label><Input type="number" value={form.weekly_salary} onChange={e => set('weekly_salary', e.target.value)} className="mt-1 h-8 text-sm" /></div>
            <div><Label className="text-xs">Días Asistidos</Label><Input type="number" step="0.5" value={form.days_attended} onChange={e => set('days_attended', e.target.value)} className="mt-1 h-8 text-sm" /></div>
            <div><Label className="text-xs">Hrs Extra / Menos</Label><Input type="number" step="0.5" value={form.extra_hours} onChange={e => set('extra_hours', e.target.value)} placeholder="+ extra, - menos" className="mt-1 h-8 text-sm" /></div>
          </div>
          {/* Preview */}
          <div className="bg-slate-50 rounded-lg p-3 text-xs space-y-1">
            <div className="flex justify-between"><span className="text-slate-500">Base ({days} días × {formatMXN(dailyRate)}/día)</span><span className="tabular-nums">{formatMXN(basePay)}</span></div>
            {extraHrs !== 0 && <div className="flex justify-between"><span className={extraHrs > 0 ? 'text-emerald-600' : 'text-red-500'}>{extraHrs > 0 ? `+${extraHrs} hrs extra` : `${extraHrs} hrs menos`} × {formatMXN(hourlyRate)}/hr</span><span className={`tabular-nums font-medium ${extraHrs > 0 ? 'text-emerald-600' : 'text-red-500'}`}>{extraPay >= 0 ? '+' : ''}{formatMXN(extraPay)}</span></div>}
            <div className="flex justify-between border-t pt-1 mt-1"><span className="font-semibold text-[#F05A28]">Total estimado</span><span className="font-bold tabular-nums text-[#F05A28]">{formatMXN(totalPreview)}</span></div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button onClick={handleSave} disabled={saving || !form.worker_name}>{saving ? 'Guardando...' : 'Guardar'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


/* ══════════════════════════════════════════════
   DESTAJO TAB
   ══════════════════════════════════════════════ */
function DestajoTab({ estimateId, records, editable, onChanged }) {
  const [addOpen, setAddOpen] = useState(false);
  const [editTarget, setEditTarget] = useState(null);

  const handleDelete = async (id) => {
    try { await deleteDestajo(id); toast.success('Eliminado'); onChanged(); }
    catch { toast.error('Error'); }
  };

  return (
    <div className="space-y-3">
      {editable && <Button size="sm" variant="outline" onClick={() => setAddOpen(true)}><Plus className="h-3.5 w-3.5 mr-1" /> Agregar Destajo</Button>}
      {records.length === 0 ? (
        <Card className="rounded-xl border bg-white"><CardContent className="py-8 text-center"><HardHat className="h-8 w-8 text-slate-200 mx-auto mb-2" /><p className="text-sm text-slate-400">Sin destajos registrados</p></CardContent></Card>
      ) : (
        <Card className="rounded-xl border bg-white overflow-clip">
          <Table>
            <TableHeader className="sticky top-0 z-20">
              <TableRow className="bg-slate-50 shadow-[0_1px_0_0_rgb(226,232,240)]">
                <TableHead className="text-[9px] uppercase tracking-widest text-slate-400 font-semibold py-2.5 px-2">Vivienda</TableHead>
                <TableHead className="text-[9px] uppercase tracking-widest text-slate-400 font-semibold py-2.5 px-2">Concepto</TableHead>
                <TableHead className="text-[9px] uppercase tracking-widest text-slate-400 font-semibold py-2.5 px-2">Contratista</TableHead>
                <TableHead className="text-[9px] uppercase tracking-widest text-slate-400 font-semibold py-2.5 px-2 text-right">Pactado</TableHead>
                <TableHead className="text-[9px] uppercase tracking-widest text-emerald-600 font-semibold py-2.5 px-2 text-right">Acumulado</TableHead>
                <TableHead className="text-[9px] uppercase tracking-widest text-slate-400 font-semibold py-2.5 px-2 text-right">Saldo</TableHead>
                <TableHead className="text-[9px] uppercase tracking-widest text-slate-400 font-semibold py-2.5 px-2 text-right">Av. Ant.</TableHead>
                <TableHead className="text-[9px] uppercase tracking-widest text-slate-400 font-semibold py-2.5 px-2 text-right">Av. Act.</TableHead>
                <TableHead className="text-[9px] uppercase tracking-widest text-slate-400 font-semibold py-2.5 px-2 text-right">Sem.</TableHead>
                <TableHead className="text-[9px] uppercase tracking-widest text-[#F05A28] font-semibold py-2.5 px-2 text-right">Monto Sem.</TableHead>
                {editable && <TableHead className="w-16" />}
              </TableRow>
            </TableHeader>
            <TableBody>
              {records.map(r => {
                const isAmount = r.pricing_type === 'amount';
                const agreed = r.agreed_total_price || 0;
                const prevPct = r.previous_progress || 0;
                const currPct = r.current_progress || 0;
                let prevAmt, currAmt, weeklyPct;
                if (isAmount && agreed > 0) {
                  prevAmt = 0;
                  currAmt = r.weekly_estimated_amount || 0;
                  weeklyPct = agreed > 0 ? (currAmt / agreed * 100) : 0;
                } else {
                  prevAmt = agreed > 0 ? agreed * prevPct / 100 : 0;
                  currAmt = agreed > 0 ? agreed * currPct / 100 : 0;
                  weeklyPct = (r.weekly_progress || 0);
                }
                const weeklyAmt = r.weekly_estimated_amount || 0;

                return (
                <TableRow key={r.id} className="group">
                  <TableCell className="py-2 px-2 text-[12px] font-semibold whitespace-nowrap">{r.property_lot ? `Lt ${r.property_lot} Mz ${r.property_block}` : '—'}</TableCell>
                  <TableCell className="py-2 px-2 text-[12px] font-medium">{r.concept_name || '—'}</TableCell>
                  <TableCell className="py-2 px-2 text-[11px] text-slate-500">{r.contractor_name || '—'}</TableCell>
                  <TableCell className="py-2 px-2 text-right text-[11px] tabular-nums">{formatMXN(agreed)}</TableCell>
                  <TableCell className="py-2 px-2 text-right text-[11px] tabular-nums text-emerald-700 font-medium">
                    <div>{formatMXN(r.accumulated_amount || 0)}</div>
                    {agreed > 0 && <div className="text-[9px] text-emerald-500">{((r.accumulated_amount || 0) / agreed * 100).toFixed(1)}%</div>}
                  </TableCell>
                  <TableCell className="py-2 px-2 text-right text-[11px] tabular-nums text-slate-500">
                    {formatMXN(r.remaining_amount || 0)}
                  </TableCell>
                  <TableCell className="py-2 px-2 text-right text-[11px] tabular-nums">
                    {isAmount ? (
                      <div><span className="text-slate-400">—</span></div>
                    ) : (
                      <div><span>{prevPct}%</span><br/><span className="text-[9px] text-slate-400">{formatMXNCompact(prevAmt)}</span></div>
                    )}
                  </TableCell>
                  <TableCell className="py-2 px-2 text-right text-[11px] tabular-nums">
                    {isAmount ? (
                      <div><span className="font-medium">{agreed > 0 ? `${(currAmt / agreed * 100).toFixed(1)}%` : '—'}</span><br/><span className="text-[9px] text-slate-400">{formatMXNCompact(currAmt)}</span></div>
                    ) : (
                      <div><span className="font-medium">{currPct}%</span><br/><span className="text-[9px] text-slate-400">{formatMXNCompact(currAmt)}</span></div>
                    )}
                  </TableCell>
                  <TableCell className="py-2 px-2 text-right text-[11px] tabular-nums font-semibold">
                    {isAmount ? (
                      <div><span>{agreed > 0 ? `${(weeklyAmt / agreed * 100).toFixed(1)}%` : '—'}</span></div>
                    ) : (
                      <span>{weeklyPct.toFixed(1)}%</span>
                    )}
                  </TableCell>
                  <TableCell className="py-2 px-2 text-right text-[12px] tabular-nums font-bold text-[#F05A28]">{formatMXN(weeklyAmt)}</TableCell>
                  {editable && <TableCell className="py-2 px-2">
                    <div className="flex gap-0.5 opacity-0 group-hover:opacity-100">
                      <Button variant="ghost" size="icon" className="h-6 w-6 text-slate-400" onClick={() => setEditTarget(r)}><Pencil className="h-3 w-3" /></Button>
                      <Button variant="ghost" size="icon" className="h-6 w-6 text-slate-400 hover:text-red-500" onClick={() => handleDelete(r.id)}><Trash2 className="h-3 w-3" /></Button>
                    </div>
                  </TableCell>}
                </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Card>
      )}
      {(addOpen || editTarget) && (
        <DestajoForm
          estimateId={estimateId}
          initial={editTarget}
          onClose={() => { setAddOpen(false); setEditTarget(null); }}
          onSaved={() => { setAddOpen(false); setEditTarget(null); onChanged(); }}
        />
      )}
    </div>
  );
}

function DestajoForm({ estimateId, initial, onClose, onSaved }) {
  const isEdit = !!initial;
  const [contracts, setContracts] = useState([]);
  const [form, setForm] = useState(initial || { contract_id: '', concept_name: '', contractor_name: '', pricing_type: 'progress', agreed_total_price: '', previous_progress: '', current_progress: '', direct_amount: '', notes: '' });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  useEffect(() => { fetchContracts({ status: 'active' }).then(setContracts).catch(() => {}); }, []);

  const selectContract = async (cid) => {
    const c = contracts.find(x => x.id === cid);
    if (c) {
      // Get latest progress from contract detail
      let lastProgress = 0;
      try {
        const detail = await fetchContract(cid);
        const entries = detail.destajos_detail || [];
        if (entries.length > 0) {
          lastProgress = entries[entries.length - 1].current_progress || 0;
        }
      } catch {}
      setForm(f => ({ ...f, contract_id: c.id, property_id: c.property_id, concept_name: c.concept_name, contractor_name: c.contractor_name || '', agreed_total_price: c.agreed_amount || '', pricing_type: 'progress', previous_progress: lastProgress, current_progress: lastProgress }));
    }
  };

  // Preview calculation
  const ptype = form.pricing_type;
  const prev = parseFloat(form.previous_progress) || 0;
  const curr = parseFloat(form.current_progress) || 0;
  const agreed = parseFloat(form.agreed_total_price) || 0;
  const directAmt = parseFloat(form.direct_amount) || 0;
  const weeklyProg = curr - prev;
  const weeklyAmount = ptype === 'amount' ? directAmt : (weeklyProg > 0 && agreed > 0 ? Math.round(weeklyProg * agreed / 100 * 100) / 100 : 0);

  const handleSave = async () => {
    setSaving(true);
    try {
      const d = {
        contract_id: form.contract_id || null,
        property_id: form.property_id || null,
        concept_name: form.concept_name,
        contractor_name: form.contractor_name,
        pricing_type: ptype,
        agreed_total_price: agreed,
        agreed_unit_price: 0,
        previous_progress: ptype === 'progress' ? prev : 0,
        current_progress: ptype === 'progress' ? curr : 0,
        quantity: ptype === 'amount' ? directAmt : 0,
        notes: form.notes,
      };
      if (isEdit) await updateDestajo(initial.id, d);
      else await addDestajo(estimateId, d);
      toast.success(isEdit ? 'Actualizado' : 'Agregado');
      onSaved();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Error'); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open onOpenChange={v => { if (!v) onClose(); }}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{isEdit ? 'Editar' : 'Agregar'} Destajo</DialogTitle>
          {isEdit && form.property_lot && <p className="text-xs text-slate-400">Lt {form.property_lot} Mz {form.property_block}</p>}
        </DialogHeader>
        <div className="space-y-3 py-2">
          {!isEdit && contracts.length > 0 && (
            <div>
              <Label className="text-xs">Seleccionar contrato de destajo</Label>
              <Select value={form.contract_id} onValueChange={selectContract}>
                <SelectTrigger className="mt-1 h-8 text-xs"><SelectValue placeholder="Seleccionar contrato..." /></SelectTrigger>
                <SelectContent>
                  {contracts.map(c => <SelectItem key={c.id} value={c.id}>Lt {c.property_lot} Mz {c.property_block} — {c.concept_name} ({formatMXN(c.agreed_amount)})</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs">Concepto / Partida</Label><Input value={form.concept_name} onChange={e => set('concept_name', e.target.value)} placeholder="Colocación de pisos" className="mt-1 h-8 text-sm" /></div>
            <div><Label className="text-xs">Contratista</Label><Input value={form.contractor_name} onChange={e => set('contractor_name', e.target.value)} className="mt-1 h-8 text-sm" /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs">Tipo de estimación</Label>
              <Select value={form.pricing_type} onValueChange={v => set('pricing_type', v)}>
                <SelectTrigger className="mt-1 h-8 text-xs"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="progress">% de Avance</SelectItem>
                  <SelectItem value="amount">Monto Directo</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div><Label className="text-xs">Monto Pactado Total</Label><Input type="number" value={form.agreed_total_price} onChange={e => set('agreed_total_price', e.target.value)} className="mt-1 h-8 text-sm" /></div>
          </div>
          {ptype === 'progress' ? (
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Avance Anterior (%)</Label><Input type="number" value={form.previous_progress} onChange={e => set('previous_progress', e.target.value)} className="mt-1 h-8 text-sm" /></div>
              <div><Label className="text-xs">Avance Actual (%)</Label><Input type="number" value={form.current_progress} onChange={e => set('current_progress', e.target.value)} className="mt-1 h-8 text-sm" /></div>
            </div>
          ) : (
            <div><Label className="text-xs">Monto a Estimar esta Semana</Label><Input type="number" value={form.direct_amount} onChange={e => set('direct_amount', e.target.value)} className="mt-1 h-8 text-sm" placeholder="$0.00" /></div>
          )}
          {/* Preview */}
          <div className="bg-slate-50 rounded-lg p-3 text-xs space-y-1">
            {ptype === 'progress' && (
              <>
                <div className="flex justify-between"><span className="text-slate-500">Avance semanal</span><span className="tabular-nums font-medium">{weeklyProg.toFixed(1)}%</span></div>
                <div className="flex justify-between"><span className="text-slate-500">Sobre monto pactado</span><span className="tabular-nums">{formatMXN(agreed)}</span></div>
              </>
            )}
            <div className="flex justify-between border-t pt-1"><span className="font-semibold text-[#F05A28]">Monto estimado</span><span className="font-bold tabular-nums text-[#F05A28]">{formatMXN(weeklyAmount)}</span></div>
          </div>
          <div><Label className="text-xs">Notas</Label><Input value={form.notes} onChange={e => set('notes', e.target.value)} placeholder="Opcional" className="mt-1 h-8 text-sm" /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button onClick={handleSave} disabled={saving || !form.concept_name}>{saving ? 'Guardando...' : 'Guardar'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}



/* ══════════════════════════════════════════════
   PERSONNEL LIST
   ══════════════════════════════════════════════ */
function PersonnelList({ canEdit }) {
  const [personnel, setPersonnel] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAll, setShowAll] = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  const [editTarget, setEditTarget] = useState(null);

  const load = useCallback(async () => {
    try { setPersonnel(await fetchPersonnel(!showAll)); }
    catch { toast.error('Error al cargar personal'); }
    finally { setLoading(false); }
  }, [showAll]);

  useEffect(() => { load(); }, [load]);

  const handleDeactivate = async (id) => {
    try { await deactivatePerson(id); toast.success('Marcado como inactivo'); load(); }
    catch { toast.error('Error'); }
  };

  if (loading) return <Skeleton className="h-[300px] rounded-xl" />;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <h2 className="text-lg font-semibold text-slate-800">Personal</h2>
          <Badge variant="outline" className="text-[10px]">{personnel.length} {showAll ? 'total' : 'activos'}</Badge>
          <label className="flex items-center gap-1.5 cursor-pointer text-[11px] text-slate-500">
            <input type="checkbox" checked={showAll} onChange={e => setShowAll(e.target.checked)} className="rounded border-slate-300" />
            Mostrar inactivos
          </label>
        </div>
        {canEdit && <Button size="sm" onClick={() => setFormOpen(true)}><Plus className="h-3.5 w-3.5 mr-1" /> Nuevo Trabajador</Button>}
      </div>
      {personnel.length === 0 ? (
        <Card className="rounded-xl border bg-white"><CardContent className="py-10 text-center"><Users className="h-10 w-10 text-slate-200 mx-auto mb-2" /><p className="text-sm text-slate-400">Sin personal registrado</p></CardContent></Card>
      ) : (
        <Card className="rounded-xl border bg-white overflow-clip">
          <Table>
            <TableHeader className="sticky top-0 z-20">
              <TableRow className="bg-slate-50 shadow-[0_1px_0_0_rgb(226,232,240)]">
                <TableHead className="text-[9px] uppercase tracking-widest text-slate-400 font-semibold py-2.5 px-3">Nombre</TableHead>
                <TableHead className="text-[9px] uppercase tracking-widest text-slate-400 font-semibold py-2.5 px-3">Teléfono</TableHead>
                <TableHead className="text-[9px] uppercase tracking-widest text-slate-400 font-semibold py-2.5 px-3">Especialidad</TableHead>
                <TableHead className="text-[9px] uppercase tracking-widest text-slate-400 font-semibold py-2.5 px-3">Cuadrilla</TableHead>
                <TableHead className="text-[9px] uppercase tracking-widest text-slate-400 font-semibold py-2.5 px-3 text-right">Sueldo Semanal</TableHead>
                <TableHead className="text-[9px] uppercase tracking-widest text-slate-400 font-semibold py-2.5 px-3">Estatus</TableHead>
                {canEdit && <TableHead className="w-20" />}
              </TableRow>
            </TableHeader>
            <TableBody>
              {personnel.map(p => (
                <TableRow key={p.id} className={`group ${p.status === 'inactive' ? 'opacity-50' : ''}`}>
                  <TableCell className="py-2 px-3 text-[12px] font-medium">{p.full_name}</TableCell>
                  <TableCell className="py-2 px-3 text-[11px] text-slate-500">{p.phone || '—'}</TableCell>
                  <TableCell className="py-2 px-3 text-[11px] text-slate-500">{p.role_or_specialty || '—'}</TableCell>
                  <TableCell className="py-2 px-3 text-[11px] text-slate-500">{p.crew || '—'}</TableCell>
                  <TableCell className="py-2 px-3 text-right text-[12px] tabular-nums font-semibold">{formatMXN(p.weekly_salary)}</TableCell>
                  <TableCell className="py-2 px-3"><Badge variant="outline" className={`text-[9px] ${p.status === 'active' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-slate-100 text-slate-500 border-slate-200'}`}>{p.status === 'active' ? 'Activo' : 'Inactivo'}</Badge></TableCell>
                  {canEdit && <TableCell className="py-2 px-3">
                    <div className="flex gap-0.5 opacity-0 group-hover:opacity-100">
                      <Button variant="ghost" size="icon" className="h-6 w-6 text-slate-400" onClick={() => setEditTarget(p)}><Pencil className="h-3 w-3" /></Button>
                      {p.status === 'active' && <Button variant="ghost" size="icon" className="h-6 w-6 text-slate-400 hover:text-red-500" onClick={() => handleDeactivate(p.id)}><Trash2 className="h-3 w-3" /></Button>}
                    </div>
                  </TableCell>}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}
      {(formOpen || editTarget) && (
        <PersonForm initial={editTarget} onClose={() => { setFormOpen(false); setEditTarget(null); }} onSaved={() => { setFormOpen(false); setEditTarget(null); load(); }} />
      )}
    </div>
  );
}

function PersonForm({ initial, onClose, onSaved }) {
  const isEdit = !!initial;
  const [allPersonnel, setAllPersonnel] = useState([]);
  const [form, setForm] = useState(initial || { full_name: '', phone: '', role_or_specialty: '', crew: '', weekly_salary: '', hire_date: '', notes: '' });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  useEffect(() => { fetchPersonnel(false).then(setAllPersonnel).catch(() => {}); }, []);

  // Get unique roles and crews sorted alphabetically
  const existingRoles = [...new Set(allPersonnel.map(p => p.role_or_specialty).filter(Boolean))].sort((a, b) => a.localeCompare(b, 'es'));
  const existingCrews = [...new Set(allPersonnel.map(p => p.crew).filter(Boolean))].sort((a, b) => a.localeCompare(b, 'es'));

  const handleRoleChange = (v) => {
    set('role_or_specialty', v === '__new__' ? '' : v);
  };

  const handleCrewChange = (v) => {
    set('crew', v === '__new__' ? '' : v);
  };
  const handleSave = async () => {
    setSaving(true);
    try {
      const d = { ...form, weekly_salary: parseFloat(form.weekly_salary) || 0 };
      if (isEdit) await updatePerson(initial.id, d);
      else await createPerson(d);
      toast.success(isEdit ? 'Actualizado' : 'Trabajador registrado');
      onSaved();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Error'); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open onOpenChange={v => { if (!v) onClose(); }}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader><DialogTitle>{isEdit ? 'Editar' : 'Nuevo'} Trabajador</DialogTitle></DialogHeader>
        <div className="space-y-3 py-2">
          <div><Label className="text-xs">Nombre completo</Label><Input value={form.full_name} onChange={e => set('full_name', e.target.value)} className="mt-1 h-8 text-sm" /></div>
          <div><Label className="text-xs">Teléfono (10 dígitos)</Label><Input value={form.phone} onChange={e => set('phone', e.target.value)} placeholder="5551020707" className="mt-1 h-8 text-sm" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs">Especialidad / Puesto</Label>
              <Select value={existingRoles.includes(form.role_or_specialty) ? form.role_or_specialty : '__new__'} onValueChange={handleRoleChange}>
                <SelectTrigger className="mt-1 h-8 text-xs"><SelectValue placeholder="Seleccionar..." /></SelectTrigger>
                <SelectContent>
                  {existingRoles.map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                  <SelectItem value="__new__">+ Nuevo puesto...</SelectItem>
                </SelectContent>
              </Select>
              {(!existingRoles.includes(form.role_or_specialty)) && (
                <Input value={form.role_or_specialty} onChange={e => set('role_or_specialty', e.target.value)} placeholder="Escribe el nuevo puesto" className="mt-1 h-8 text-sm" />
              )}
            </div>
            <div><Label className="text-xs">Cuadrilla</Label>
              <Select value={existingCrews.includes(form.crew) ? form.crew : '__new__'} onValueChange={handleCrewChange}>
                <SelectTrigger className="mt-1 h-8 text-xs"><SelectValue placeholder="Seleccionar..." /></SelectTrigger>
                <SelectContent>
                  {existingCrews.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  <SelectItem value="__new__">+ Nueva cuadrilla...</SelectItem>
                </SelectContent>
              </Select>
              {(!existingCrews.includes(form.crew)) && (
                <Input value={form.crew} onChange={e => set('crew', e.target.value)} placeholder="Nombre de la nueva cuadrilla" className="mt-1 h-8 text-sm" />
              )}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs">Sueldo Semanal</Label><Input type="number" value={form.weekly_salary} onChange={e => set('weekly_salary', e.target.value)} className="mt-1 h-8 text-sm" /></div>
            <div><Label className="text-xs">Fecha de ingreso</Label><Input type="date" value={form.hire_date} onChange={e => set('hire_date', e.target.value)} className="mt-1 h-8 text-sm" /></div>
          </div>
          {isEdit && (
            <div>
              <Label className="text-xs">Estatus</Label>
              <Select value={form.status || 'active'} onValueChange={v => set('status', v)}>
                <SelectTrigger className="mt-1 h-8 text-xs"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Activo</SelectItem>
                  <SelectItem value="inactive">Inactivo</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}
          <div><Label className="text-xs">Notas</Label><Input value={form.notes} onChange={e => set('notes', e.target.value)} className="mt-1 h-8 text-sm" /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button onClick={handleSave} disabled={saving || !form.full_name}>{saving ? 'Guardando...' : 'Guardar'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


/* ══════════════════════════════════════════════
   CONTRACTS LIST (Destajos Pactados)
   ══════════════════════════════════════════════ */
function ContractsList({ canEdit }) {
  const [contracts, setContracts] = useState([]);
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [formOpen, setFormOpen] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [detailContract, setDetailContract] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const load = useCallback(async () => {
    try {
      const [c, p] = await Promise.all([fetchContracts(), fetchProperties()]);
      setContracts(c);
      setProperties(p);
    } catch { toast.error('Error al cargar contratos'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openDetail = async (c) => {
    setDetailLoading(true);
    try { setDetailContract(await fetchContract(c.id)); }
    catch { toast.error('Error al cargar detalle'); }
    finally { setDetailLoading(false); }
  };

  if (loading) return <Skeleton className="h-[300px] rounded-xl" />;

  const totalPactado = contracts.reduce((s, c) => s + (c.agreed_amount || 0), 0);
  const totalEstimado = contracts.reduce((s, c) => s + (c.total_estimated || 0), 0);
  const totalPendiente = contracts.reduce((s, c) => s + (c.pending || 0), 0);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-slate-800">Destajos Pactados</h2>
        {canEdit && <Button size="sm" onClick={() => setFormOpen(true)}><Plus className="h-3.5 w-3.5 mr-1" /> Nuevo Contrato</Button>}
      </div>

      <div className="grid grid-cols-3 gap-3">
        <KPICard label="Total Pactado" value={formatMXNCompact(totalPactado)} icon={DollarSign} compact testId="kpi-pactado" />
        <KPICard label="Total Estimado" value={formatMXNCompact(totalEstimado)} icon={ClipboardList} compact testId="kpi-estimado" />
        <KPICard label="Pendiente" value={formatMXNCompact(totalPendiente)} icon={Clock} compact testId="kpi-pendiente" />
      </div>

      {contracts.length === 0 ? (
        <Card className="rounded-xl border bg-white"><CardContent className="py-10 text-center"><HardHat className="h-10 w-10 text-slate-200 mx-auto mb-2" /><p className="text-sm text-slate-400">Sin contratos de destajo</p></CardContent></Card>
      ) : (
        <Card className="rounded-xl border bg-white overflow-clip">
          <Table>
            <TableHeader className="sticky top-0 z-20">
              <TableRow className="bg-slate-50 shadow-[0_1px_0_0_rgb(226,232,240)]">
                <TableHead className="text-[9px] uppercase tracking-widest text-slate-400 font-semibold py-2.5 px-3">Vivienda</TableHead>
                <TableHead className="text-[9px] uppercase tracking-widest text-slate-400 font-semibold py-2.5 px-3">Concepto</TableHead>
                <TableHead className="text-[9px] uppercase tracking-widest text-slate-400 font-semibold py-2.5 px-3">Responsable</TableHead>
                <TableHead className="text-[9px] uppercase tracking-widest text-slate-400 font-semibold py-2.5 px-3 text-right">Pactado</TableHead>
                <TableHead className="text-[9px] uppercase tracking-widest text-slate-400 font-semibold py-2.5 px-3 text-right">Estimado</TableHead>
                <TableHead className="text-[9px] uppercase tracking-widest text-[#F05A28] font-semibold py-2.5 px-3 text-right">Pendiente</TableHead>
                <TableHead className="text-[9px] uppercase tracking-widest text-slate-400 font-semibold py-2.5 px-3">Estatus</TableHead>
                {canEdit && <TableHead className="w-12" />}
              </TableRow>
            </TableHeader>
            <TableBody>
              {contracts.map(c => (
                <TableRow key={c.id} className="group cursor-pointer table-row-hover" onClick={() => openDetail(c)}>
                  <TableCell className="py-2 px-3 text-[12px] font-medium">Lt {c.property_lot} Mz {c.property_block}</TableCell>
                  <TableCell className="py-2 px-3 text-[11px] text-slate-600">{c.concept_name}</TableCell>
                  <TableCell className="py-2 px-3 text-[11px] text-slate-500">{c.contractor_name || '—'}</TableCell>
                  <TableCell className="py-2 px-3 text-right text-[11px] tabular-nums font-semibold">{formatMXN(c.agreed_amount)}</TableCell>
                  <TableCell className="py-2 px-3 text-right text-[11px] tabular-nums">{formatMXN(c.total_estimated)}</TableCell>
                  <TableCell className="py-2 px-3 text-right text-[12px] tabular-nums font-bold text-[#F05A28]">{formatMXN(c.pending)}</TableCell>
                  <TableCell className="py-2 px-3"><Badge variant="outline" className={`text-[9px] ${c.status === 'active' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : c.status === 'completed' ? 'bg-blue-50 text-blue-700 border-blue-200' : 'bg-slate-100 text-slate-500'}`}>{c.status === 'active' ? 'Activo' : c.status === 'completed' ? 'Completado' : 'Cancelado'}</Badge></TableCell>
                  {canEdit && <TableCell className="py-2 px-3">
                    <Button variant="ghost" size="icon" className="h-6 w-6 text-slate-400 opacity-0 group-hover:opacity-100" onClick={() => setEditTarget(c)}><Pencil className="h-3 w-3" /></Button>
                  </TableCell>}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}
      {(formOpen || editTarget) && (
        <ContractForm initial={editTarget} properties={properties} onClose={() => { setFormOpen(false); setEditTarget(null); }} onSaved={() => { setFormOpen(false); setEditTarget(null); load(); }} />
      )}
      {detailContract && (
        <ContractDetailDialog contract={detailContract} loading={detailLoading} onClose={() => setDetailContract(null)} />
      )}
    </div>
  );
}

function ContractDetailDialog({ contract: c, loading, onClose }) {
  const destajos = c.destajos_detail || [];
  const pactado = c.agreed_amount || 0;
  const estimado = c.total_estimated || 0;
  const pendiente = c.pending || 0;
  const pctUsado = pactado > 0 ? Math.round(estimado / pactado * 100) : 0;

  const STATUS_LABELS = { draft: 'En Cálculo', approved: 'Aprobada', partially_paid: 'Pago Parcial', paid: 'Pagada', cancelled: 'Cancelada' };

  return (
    <Dialog open onOpenChange={v => { if (!v) onClose(); }}>
      <DialogContent className="sm:max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="contract-detail-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <HardHat className="h-5 w-5 text-[#F05A28]" /> {c.concept_name}
          </DialogTitle>
          <p className="text-xs text-slate-400">Lt {c.property_lot} Mz {c.property_block} · {c.contractor_name || 'Sin responsable'}</p>
        </DialogHeader>
        <div className="space-y-4 py-2">
          {/* Summary */}
          <div className="grid grid-cols-3 gap-3">
            <div className="bg-slate-50 rounded-lg p-3 text-center">
              <p className="text-[9px] uppercase tracking-widest text-slate-400">Pactado</p>
              <p className="text-[15px] font-bold tabular-nums text-slate-700" style={{ fontFamily: 'Space Grotesk' }}>{formatMXN(pactado)}</p>
            </div>
            <div className="bg-slate-50 rounded-lg p-3 text-center">
              <p className="text-[9px] uppercase tracking-widest text-slate-400">Estimado</p>
              <p className="text-[15px] font-bold tabular-nums text-slate-700" style={{ fontFamily: 'Space Grotesk' }}>{formatMXN(estimado)}</p>
              <p className="text-[9px] text-slate-400">{pctUsado}% del pactado</p>
            </div>
            <div className="bg-slate-50 rounded-lg p-3 text-center border-[#F05A28]/20 border">
              <p className="text-[9px] uppercase tracking-widest text-[#F05A28]">Pendiente</p>
              <p className={`text-[15px] font-bold tabular-nums ${pendiente > 0 ? 'text-[#F05A28]' : 'text-emerald-600'}`} style={{ fontFamily: 'Space Grotesk' }}>{formatMXN(pendiente)}</p>
            </div>
          </div>

          {/* Progress bar */}
          <div className="flex items-center gap-3">
            <span className="text-[10px] text-slate-400 w-16 text-right shrink-0">Avance</span>
            <div className="flex-1 h-2.5 rounded-full bg-slate-100 overflow-hidden">
              <div className="h-full rounded-full bg-[#F05A28] transition-all" style={{ width: `${Math.min(pctUsado, 100)}%` }} />
            </div>
            <span className="text-[10px] tabular-nums font-semibold text-[#F05A28] w-10 shrink-0">{pctUsado}%</span>
          </div>

          {/* Destajos entries */}
          <div>
            <p className="text-[10px] font-semibold uppercase tracking-widest text-slate-400 mb-2">Historial de Estimaciones ({destajos.length})</p>
            {destajos.length === 0 ? (
              <p className="text-[11px] text-slate-400">Sin estimaciones vinculadas.</p>
            ) : (
              <Card className="rounded-lg border overflow-clip">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50">
                      <TableHead className="text-[9px] uppercase text-slate-400 font-semibold py-2 px-3">Semana</TableHead>
                      <TableHead className="text-[9px] uppercase text-slate-400 font-semibold py-2 px-3">Estatus</TableHead>
                      <TableHead className="text-[9px] uppercase text-slate-400 font-semibold py-2 px-3 text-right">Av. Ant.</TableHead>
                      <TableHead className="text-[9px] uppercase text-slate-400 font-semibold py-2 px-3 text-right">Av. Act.</TableHead>
                      <TableHead className="text-[9px] uppercase text-slate-400 font-semibold py-2 px-3 text-right">Avance Sem.</TableHead>
                      <TableHead className="text-[9px] uppercase text-[#F05A28] font-semibold py-2 px-3 text-right">Monto</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {destajos.map(d => (
                      <TableRow key={d.id}>
                        <TableCell className="py-2 px-3 text-[11px] font-medium">{d.week_label || formatDate(d.estimate_start_date)}</TableCell>
                        <TableCell className="py-2 px-3"><Badge variant="outline" className={`text-[8px] ${d.estimate_status === 'paid' ? 'bg-blue-50 text-blue-700' : d.estimate_status === 'approved' ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-500'}`}>{STATUS_LABELS[d.estimate_status] || d.estimate_status}</Badge></TableCell>
                        <TableCell className="py-2 px-3 text-right text-[11px] tabular-nums">{d.pricing_type === 'amount' ? '—' : `${d.previous_progress || 0}%`}</TableCell>
                        <TableCell className="py-2 px-3 text-right text-[11px] tabular-nums">{d.pricing_type === 'amount' ? '—' : `${d.current_progress || 0}%`}</TableCell>
                        <TableCell className="py-2 px-3 text-right text-[11px] tabular-nums font-semibold">{d.pricing_type === 'amount' ? <span className="text-[9px] text-slate-400">Monto</span> : `${(d.weekly_progress || 0).toFixed(1)}%`}</TableCell>
                        <TableCell className="py-2 px-3 text-right text-[12px] tabular-nums font-bold text-[#F05A28]">{formatMXN(d.weekly_estimated_amount || 0)}</TableCell>
                      </TableRow>
                    ))}
                    {/* Total row */}
                    <TableRow className="bg-slate-50 border-t-2">
                      <TableCell colSpan={5} className="py-2 px-3 text-[11px] font-semibold text-right">Total Estimado</TableCell>
                      <TableCell className="py-2 px-3 text-right text-[13px] tabular-nums font-bold text-[#F05A28]">{formatMXN(estimado)}</TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </Card>
            )}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cerrar</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ContractForm({ initial, properties, onClose, onSaved }) {
  const isEdit = !!initial;
  const [catalog, setCatalog] = useState([]);
  const [form, setForm] = useState(initial || { property_id: '', work_catalog_item_id: '', concept_name: '', contractor_name: '', agreed_amount: '', notes: '' });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  useEffect(() => { fetchWorkCatalog(null, true).then(setCatalog).catch(() => {}); }, []);

  const selectCatalogItem = (catName) => {
    setForm(f => ({ ...f, concept_name: catName }));
  };

  // Get unique category names (partidas) in catalog order
  const seen = new Set();
  const partidas = catalog.reduce((arr, c) => {
    if (!seen.has(c.category_name)) { seen.add(c.category_name); arr.push(c.category_name); }
    return arr;
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      const d = { ...form, agreed_amount: parseFloat(form.agreed_amount) || 0 };
      if (isEdit) await updateContract(initial.id, d);
      else await createContract(d);
      toast.success(isEdit ? 'Actualizado' : 'Contrato creado');
      onSaved();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Error'); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open onOpenChange={v => { if (!v) onClose(); }}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader><DialogTitle>{isEdit ? 'Editar' : 'Nuevo'} Contrato de Destajo</DialogTitle></DialogHeader>
        <div className="space-y-3 py-2">
          <div>
            <Label className="text-xs">Vivienda</Label>
            <Select value={form.property_id} onValueChange={v => set('property_id', v)}>
              <SelectTrigger className="mt-1 h-8 text-xs"><SelectValue placeholder="Seleccionar vivienda" /></SelectTrigger>
              <SelectContent>
                {properties.map(p => <SelectItem key={p.id} value={p.id}>Lt {p.lot} Mz {p.block} — {p.name || p.modelo}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Partida del Catálogo de Obra</Label>
            <Select value={form.concept_name || ''} onValueChange={selectCatalogItem}>
              <SelectTrigger className="mt-1 h-8 text-xs"><SelectValue placeholder="Seleccionar partida..." /></SelectTrigger>
              <SelectContent>
                {partidas.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}
              </SelectContent>
            </Select>
            <p className="text-[9px] text-slate-400 mt-1">O escribe manualmente abajo</p>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs">Concepto (nombre)</Label><Input value={form.concept_name} onChange={e => set('concept_name', e.target.value)} className="mt-1 h-8 text-sm" /></div>
            <div><Label className="text-xs">Responsable</Label><Input value={form.contractor_name} onChange={e => set('contractor_name', e.target.value)} className="mt-1 h-8 text-sm" /></div>
          </div>
          <div><Label className="text-xs">Monto Pactado</Label><Input type="number" value={form.agreed_amount} onChange={e => set('agreed_amount', e.target.value)} className="mt-1 h-8 text-sm" /></div>
          <div><Label className="text-xs">Notas</Label><Input value={form.notes} onChange={e => set('notes', e.target.value)} className="mt-1 h-8 text-sm" /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button onClick={handleSave} disabled={saving || !form.property_id || !form.concept_name}>{saving ? 'Guardando...' : 'Guardar'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
